import React from 'react';
import {
  LayoutDashboard, CheckSquare, Users, Briefcase, Menu,
  Calendar, HelpCircle, Settings, Receipt, Clock, Coins,
} from 'lucide-react';

/**
 * MobileBottomNav — sticky primary navigation on mobile. Role-aware so
 * accountants land on their Accounting stack instead of Tasks/Clients,
 * HR gets Organisation front-and-centre, employees get Attendance in
 * the first row.
 */
export default function MobileBottomNav({
  activeMenu,
  setActiveMenu,
  onMenuOpen,
  portalType = 'admin',
  role = null,
}) {
  const getNavItems = () => {
    if (portalType === 'admin') {
      if (role === 'accountant') {
        return [
          { key: 'accounting', label: 'Accounting', icon: Receipt },
          { key: 'clients', label: 'Clients', icon: Briefcase },
          { key: 'data-health', label: 'Health', icon: HelpCircle },
          { key: 'more', label: 'More', icon: Menu },
        ];
      }
      if (role === 'hr') {
        return [
          { key: 'dashboard', label: 'Home', icon: LayoutDashboard },
          { key: 'organisation', label: 'People', icon: Users },
          { key: 'hr', label: 'HR', icon: Coins },
          { key: 'clients', label: 'Clients', icon: Briefcase },
          { key: 'more', label: 'More', icon: Menu },
        ];
      }
      return [
        { key: 'dashboard', label: 'Home', icon: LayoutDashboard },
        { key: 'tasks', label: 'Tasks', icon: CheckSquare },
        { key: 'accounting', label: 'Money', icon: Receipt },
        { key: 'clients', label: 'Clients', icon: Briefcase },
        { key: 'more', label: 'More', icon: Menu },
      ];
    }
    if (portalType === 'employee') {
      return [
        { key: 'dashboard', label: 'Home', icon: LayoutDashboard },
        { key: 'tasks', label: 'Tasks', icon: CheckSquare },
        { key: 'attendance', label: 'Attend', icon: Clock },
        { key: 'calendars', label: 'Calendar', icon: Calendar },
        { key: 'more', label: 'More', icon: Menu },
      ];
    }
    if (portalType === 'client') {
      return [
        { key: 'dashboard', label: 'Home', icon: LayoutDashboard },
        { key: 'tasks', label: 'Tasks', icon: CheckSquare },
        { key: 'helpdesk', label: 'Support', icon: HelpCircle },
        { key: 'settings', label: 'Settings', icon: Settings },
      ];
    }
    return [];
  };

  const navItems = getNavItems();
  const handleNavClick = (key) => {
    if (key === 'more') onMenuOpen?.();
    else setActiveMenu(key);
  };

  const activeClasses = {
    admin: 'text-[#FF3D7F] bg-pink-50',
    employee: 'text-amber-600 bg-amber-50',
    client: 'text-blue-600 bg-blue-50',
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-2xl border-t border-neutral-200/70 z-50"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeMenu === item.key || (item.key === 'accounting' && activeMenu === 'financial_dashboard');
          return (
            <button
              key={item.key}
              onClick={() => handleNavClick(item.key)}
              className={`relative flex flex-col items-center justify-center flex-1 min-h-[44px] py-1 px-2 rounded-xl transition-all ${
                isActive
                  ? (activeClasses[portalType] || activeClasses.admin) + ' scale-105'
                  : 'text-neutral-500 hover:text-neutral-800 active:bg-neutral-100'
              }`}
              data-testid={`mobile-nav-${item.key}`}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              {isActive && (
                <span className="absolute top-0 h-0.5 w-6 rounded-full bg-current" />
              )}
              <Icon className={`w-5 h-5 ${isActive ? 'scale-110' : ''} transition-transform`} />
              <span className={`text-[10px] font-medium mt-0.5 truncate max-w-full ${isActive ? 'font-bold' : ''}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
